import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Lock, XCircle } from "lucide-react";

export const Route = createFileRoute("/pago/enlace/$token")({
  head: () => ({ meta: [{ title: "Pago seguro · El Pajar de Benito" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: EnlacePagoPage,
});

type EnlaceInfo = {
  concepto: string;
  importe: number;
  cliente_nombre: string | null;
  estado: "pendiente" | "pagado" | "cancelado" | "caducado";
  pagado_at: string | null;
};

const fmt = (n: number) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

function EnlacePagoPage() {
  const { token } = Route.useParams();
  const url = new URL(typeof window !== "undefined" ? window.location.href : "http://localhost");
  const searchOk = url.searchParams.get("ok");
  const searchKo = url.searchParams.get("ko");
  const [info, setInfo] = useState<EnlaceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_enlace_pago_publico", { _token: token });
    if (error) toast.error(error.message);
    setInfo((data as EnlaceInfo | null) ?? null);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  async function pagar() {
    setEnviando(true);
    try {
      const res = await fetch(`/api/public/enlace-pago/${token}/iniciar`, { method: "POST" });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error ?? "No se pudo iniciar el pago");
      const form = document.createElement("form");
      form.method = "POST";
      form.action = payload.url;
      for (const [k, v] of Object.entries({
        Ds_SignatureVersion: payload.Ds_SignatureVersion,
        Ds_MerchantParameters: payload.Ds_MerchantParameters,
        Ds_Signature: payload.Ds_Signature,
      })) {
        const input = document.createElement("input");
        input.type = "hidden"; input.name = k; input.value = String(v);
        form.appendChild(input);
      }
      document.body.appendChild(form); form.submit();
    } catch (e) {
      toast.error((e as Error).message);
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/30 py-12">
      <div className="mx-auto max-w-lg px-4">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold">Pago seguro</h1>
          <p className="text-sm text-muted-foreground">El Pajar de Benito</p>
        </div>

        {loading ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Cargando…</CardContent></Card>
        ) : !info ? (
          <Card><CardContent className="py-8 text-center">
            <XCircle className="mx-auto mb-2 h-10 w-10 text-destructive" />
            <p className="font-medium">Enlace no válido o caducado</p>
            <p className="text-sm text-muted-foreground">Contacta con nosotros si necesitas un enlace nuevo.</p>
          </CardContent></Card>
        ) : info.estado === "pagado" ? (
          <Card><CardContent className="py-8 text-center">
            <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-green-600" />
            <p className="font-medium">Este pago ya se ha completado</p>
            <p className="mt-1 text-sm text-muted-foreground">{info.concepto}</p>
            <p className="mt-3 text-lg font-semibold">{fmt(Number(info.importe))}</p>
          </CardContent></Card>
        ) : info.estado !== "pendiente" ? (
          <Card><CardContent className="py-8 text-center">
            <XCircle className="mx-auto mb-2 h-10 w-10 text-destructive" />
            <p className="font-medium">Este enlace está {info.estado}</p>
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="space-y-5 p-6">
              {searchOk && <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">Comprobando pago… si aparece autorizado se marcará automáticamente.</div>}
              {searchKo && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">El pago no se completó. Puedes intentarlo de nuevo.</div>}

              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Concepto</p>
                <p className="text-base font-medium">{info.concepto}</p>
              </div>
              {info.cliente_nombre && (
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">A nombre de</p>
                  <p>{info.cliente_nombre}</p>
                </div>
              )}
              <div className="rounded-md border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total a pagar</p>
                <p className="text-3xl font-semibold">{fmt(Number(info.importe))}</p>
              </div>
              <Button size="lg" className="w-full" onClick={pagar} disabled={enviando}>
                <CreditCard className="mr-2 h-5 w-5" />
                {enviando ? "Redirigiendo…" : `Pagar ${fmt(Number(info.importe))}`}
              </Button>
              <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> Pago procesado por Redsys · Tarjeta segura
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}